// Header.js - Advanced Animated Header with Fireworks
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faShoppingCart, 
  faPhone, 
  faEnvelope,
  faDownload,
  faChevronRight,
  faTimes,
  faHome,
  faInfoCircle,
  faPhoneAlt,
  faPaperPlane,
  faClock,
  faWandSparkles,
  faSparkles,
  faRocket,
  faFire,
  faBolt,
  faCrown,
  faGem,
  faStar
} from "@fortawesome/free-solid-svg-icons";
import { Offcanvas, Badge } from "react-bootstrap";
import "./Header.css";
import axios from 'axios';
import Button from 'react-bootstrap/Button';
import myIcon from '../Assets/carticon.png';
import logo from '../Assets/logo.png';
import HeaderFireworks from "../Components/HeaderFireworks";

function HeaderWhite() {
  const [cartCount, setCartCount] = useState(0);
  const [storeClose, setStoreClose] = useState(0);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [contactData, setContactData] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState("/home");
  const [isHovering, setIsHovering] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const headerRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const location = useLocation();

  // Advanced scroll with throttle
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mouse tracking for interactive effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setMousePosition({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location]);

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}content.php`);
        const { body } = response.data;
        setContactData(body.contact);
        setStoreClose(Number(body.store_close));
      } catch (error) {
        console.error("Error fetching the contact data", error);
      }
    };
    fetchContactData();
  }, []);

  useEffect(() => {
    const count = parseInt(localStorage.getItem("cartCount")) || 0;
    setCartCount(count);

    const handleStorageChange = () => {
      const count = parseInt(localStorage.getItem("cartCount")) || 0;
      setCartCount(count);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleClose = useCallback(() => setShowOffcanvas(false), []);
  const handleShow = useCallback(() => setShowOffcanvas(true), []);

  const handleDownloadPriceList = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}download_pricelist.php`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'PriceList.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download price list. Please try again later.');
    }
  }, [API_BASE_URL]);

  const navItems = useMemo(() => [
    { path: "/home", label: "Home", icon: faHome, glow: "home" },
    { path: "/quick", label: "Enquiry", icon: faPaperPlane, glow: "enquiry" },
    { path: "/about", label: "About Us", icon: faInfoCircle, glow: "about" },
    { path: "/contact", label: "Contact Us", icon: faPhoneAlt, glow: "contact" },
  ], []);

  return (
    <div 
      className={`nav-main-white ${isScrolled ? 'scrolled' : ''}`}
      ref={headerRef}
    >
      {/* Animated Pre-Header */}
      <div className="preheader-colorful">
        <div className="container">
          <div className="preheader-left">
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <span>{contactData?.email || "info@mycrackers.com"}</span>
            </div>
            <div className="info-divider"></div>
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <span>{contactData?.phonenumber || "+91 98765 43210"}</span>
            </div>
          </div>
          
          <div className="preheader-right">
            <div className="timing-item">
              <div className="timing-dot"></div>
              <span>24/7 Available</span>
            </div>
            <div className="social-icons">
              
              <a  href={contactData?.facebook_url} className="social-icon facebook" aria-label="Facebook">
                <i className="fab fa-facebook-f"></i>
                <span className="social-tooltip">Facebook</span>
              </a>
              <a href={contactData?.instagram_url} className="social-icon instagram" aria-label="Instagram">
                <i className="fab fa-instagram"></i>
                <span className="social-tooltip">Instagram</span>
              </a>
              <a href={contactData?.youtube_url} className="social-icon youtube" aria-label="YouTube">
                <i className="fab fa-youtube"></i>
                <span className="social-tooltip">YouTube</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header with Advanced Fireworks */}
      <header 
        className={`main-header-white-bg ${isScrolled ? 'header-shadow-white' : ''}`}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div className="header-bg-layer">
          <div className="header-bg-glow" style={{
            background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(229, 57, 53, 0.15), transparent 50%)`
          }}></div>
        </div>
        
        {/* ===== ADVANCED FIREWORKS OVERLAY ===== */}
        <HeaderFireworks />
        
        {/* Decorative Particles */}
        <div className="header-particles">
          {[...Array(8)].map((_, i) => (
            <span key={i} className={`floating-particle particle-${i + 1}`}></span>
          ))}
        </div>
        
        {/* Header Content */}
        <div className="container header-content">
          <Link to="/home" className="logo-wrapper">
            <img src={logo} alt="My Crackers" className="logo-img" />
          </Link>

          <nav className="nav-desktop">
            <ul className="nav-list">
              {navItems.map((item) => (
                <li key={item.path} className="nav-item">
                  <Link 
                    to={item.path} 
                    className={`nav-link-white ${activeLink === item.path ? 'active' : ''}`}
                  >
                    <span className="nav-link-icon">
                      <FontAwesomeIcon icon={item.icon} />
                    </span>
                    <span className="nav-link-text">{item.label}</span>
                    <span className="nav-link-glow"></span>
                    {activeLink === item.path && (
                      <span className="nav-link-active-dot"></span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="header-actions">
            <Button 
              className="price-list-btn-premium-white" 
              onClick={handleDownloadPriceList}
            >
              <span className="btn-particles">
                {[...Array(6)].map((_, i) => (
                  <span key={i} className={`particle particle-${i + 1}`}></span>
                ))}
              </span>
              <span className="btn-content">
                <span className="btn-icon-wrapper">
                  <FontAwesomeIcon icon={faDownload} className="btn-icon" />
                  <FontAwesomeIcon icon={faWandSparkles} className="btn-icon-hover" />
                </span>
                <span className="btn-text">Price List</span>
                <span className="btn-badge">PDF</span>
                <span className="btn-shine"></span>
              </span>
              <span className="btn-ripple"></span>
              <span className="btn-progress">
                <span className="progress-bar"></span>
              </span>
              <span className="btn-glow"></span>
            </Button>
            
            {storeClose === 0 && (
              <Link to="/cart" className="cart-link">
                <div className="cart-wrapper-white">
                  <img src={myIcon} alt="Cart" className="cart-icon" />
                  {cartCount > 0 && (
                    <Badge className="cart-badge-white">
                      {cartCount > 99 ? '99+' : cartCount}
                    </Badge>
                  )}
                  <span className="cart-pulse"></span>
                </div>
              </Link>
            )}

            <button 
              className={`mobile-menu-btn-white ${showOffcanvas ? 'active' : ''}`} 
              onClick={handleShow}
              aria-label="Menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>

        {/* Scrolled Progress Bar */}
        <div className="header-progress-bar">
          <div className="header-progress-fill" style={{
            width: `${isScrolled ? 100 : 0}%`
          }}></div>
        </div>
      </header>

      {/* Enhanced Mobile Offcanvas */}
      <Offcanvas 
        show={showOffcanvas} 
        onHide={handleClose} 
        placement="right"
        className="mobile-offcanvas-white"
        backdropClassName="offcanvas-backdrop-white"
      >
        <Offcanvas.Header className="offcanvas-header-white">
          <div className="offcanvas-logo">
            <img src={logo} alt="My Crackers" />
          </div>
          <button className="offcanvas-close-white" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
            <span className="close-tooltip">Close</span>
          </button>
        </Offcanvas.Header>
        <Offcanvas.Body className="offcanvas-body-white">
          <div className="offcanvas-greeting">
            <span className="greeting-icon">👋</span>
            <span className="greeting-text">Welcome!</span>
          </div>
          
          <ul className="mobile-nav-list">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link 
                  to={item.path} 
                  className="mobile-nav-link-white" 
                  onClick={handleClose}
                >
                  <span className="mobile-nav-icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                  <span className="mobile-nav-label">{item.label}</span>
                  <FontAwesomeIcon icon={faChevronRight} className="arrow-icon" />
                  <span className="mobile-nav-glow"></span>
                </Link>
              </li>
            ))}
          </ul>
          
          <div className="mobile-divider"></div>
          
          <div className="mobile-contact-white">
            <div className="mobile-contact-item">
              <FontAwesomeIcon icon={faEnvelope} />
              <span>{contactData?.email || "info@mycrackers.com"}</span>
            </div>
            <div className="mobile-contact-item">
              <FontAwesomeIcon icon={faPhone} />
              <span>{contactData?.phonenumber || "+91 98765 43210"}</span>
            </div>
            <div className="mobile-contact-item">
              <FontAwesomeIcon icon={faClock} />
              <span>24/7 Available</span>
            </div>
          </div>

          <Button 
            className="mobile-price-list-white" 
            onClick={() => {
              handleDownloadPriceList();
              handleClose();
            }}
          >
            <FontAwesomeIcon icon={faDownload} />
            <span>Price List</span>
            <span className="mobile-btn-badge">PDF</span>
          </Button>
          
          <div className="mobile-social">
            <a href="#" className="mobile-social-icon facebook">
              <i className="fab fa-facebook-f"></i>
            </a>
            <a href="#" className="mobile-social-icon instagram">
              <i className="fab fa-instagram"></i>
            </a>
            <a href="#" className="mobile-social-icon youtube">
              <i className="fab fa-youtube"></i>
            </a>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}

export default HeaderWhite;